import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import { action } from '@ember/object';
import { service } from '@ember/service';
import { on } from '@ember/modifier';
import type Owner from '@ember/owner';
import type RouterService from '@ember/routing/router-service';
import Icon from '../components/icon';
import EyeIcon from '~icons/lucide/eye';
import EyeOffIcon from '~icons/lucide/eye-off';
import XIcon from '~icons/lucide/x';
import GoogleIcon from '~icons/simple-icons/google';
import type Session from '../services/session';
import type ShareIntentService from '../services/share-intent';
import type PushNotifications from '../services/push-notifications';
import { apiFetch } from '../fetch';
import config from 'favoris-web/config/environment';

const LAST_EMAIL_KEY = 'favoris:last-login-email';

export default class LoginTemplate extends Component {
  @service declare session: Session;
  @service declare router: RouterService;
  @service declare shareIntent: ShareIntentService;
  @service declare pushNotifications: PushNotifications;

  @tracked email = '';
  @tracked password = '';
  @tracked error = '';
  @tracked isLoading = false;
  @tracked showPassword = false;
  @tracked rememberMe = true;
  @tracked isGoogleLoading = false;

  constructor(owner: Owner, args: Record<string, never>) {
    super(owner, args);
    const saved = localStorage.getItem(LAST_EMAIL_KEY);
    if (saved) {
      this.email = saved;
    }
  }

  @action
  updateEmail(event: Event) {
    this.email = (event.target as HTMLInputElement).value;
  }

  @action
  clearEmail() {
    this.email = '';
    localStorage.removeItem(LAST_EMAIL_KEY);
  }

  @action
  updatePassword(event: Event) {
    this.password = (event.target as HTMLInputElement).value;
  }

  @action
  toggleShowPassword() {
    this.showPassword = !this.showPassword;
  }

  @action
  toggleRememberMe() {
    this.rememberMe = !this.rememberMe;
  }

  /** Compute the OAuth callback URL for this session.
   * On native mobile the API redirects to the public appUrl so Android App
   * Links can intercept it and reopen the app. On web it falls back to the
   * current origin so the same SPA handles the callback. */
  private get oauthCallbackUrl(): string {
    const base = config.favoris.appUrl || window.location.origin;
    return `${base}/auto-login`;
  }

  @action
  async handleGoogleLogin() {
    this.error = '';
    this.isGoogleLoading = true;

    try {
      const response = await apiFetch('/api/auth/sign-in/social', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          provider: 'google',
          callbackURL: this.oauthCallbackUrl,
        }),
      });

      if (!response.ok) {
        throw new Error(`Google login unavailable (${response.status})`);
      }

      const data = (await response.json()) as { url: string };

      // On Capacitor native: open in Chrome Custom Tab so Google accepts it.
      // On web: redirect the current tab.
      const { Capacitor } = await import('@capacitor/core');
      if (Capacitor.isNativePlatform()) {
        const { Browser } = await import('@capacitor/browser');
        await Browser.open({ url: data.url });
      } else {
        window.location.href = data.url;
      }
    } catch (e) {
      this.error = e instanceof Error ? e.message : 'Google login failed';
      this.isGoogleLoading = false;
    }
  }

  @action
  async handleLogin(event: Event) {
    event.preventDefault();
    this.error = '';
    this.isLoading = true;

    try {
      await this.session.authenticate(
        'authenticator:better-auth',
        this.email,
        this.password,
        this.rememberMe
      );
      localStorage.setItem(LAST_EMAIL_KEY, this.email);

      // Request push notification permission on first login (per user)
      const userId = this.session.user?.id;
      if (userId) {
        void this.pushNotifications.requestPermissionIfNeeded(userId);
      }

      // Explicit redirect — ESA's event-based redirect may not
      // fire reliably in Vite/Embroider builds.
      const redirect = localStorage.getItem('favoris:login-redirect');
      if (redirect) {
        localStorage.removeItem('favoris:login-redirect');
        this.router.transitionTo(redirect);
      } else if (
        this.shareIntent.pendingUrl ||
        this.shareIntent.pendingImageUri
      ) {
        this.router.transitionTo('extract');
      } else {
        this.router.transitionTo('index');
      }
    } catch (e) {
      this.error = e instanceof Error ? e.message : 'Login failed';
    } finally {
      this.isLoading = false;
    }
  }

  <template>
    <div class="mx-auto max-w-md">
      <h1 class="mb-6 text-center text-3xl font-bold">Login</h1>

      <div class="bg-base-100 rounded-box p-8 shadow">
        <form {{on "submit" this.handleLogin}}>
          {{#if this.error}}
            <div class="alert alert-error mb-4">
              <span>{{this.error}}</span>
            </div>
          {{/if}}

          <div class="form-control mb-4">
            <label class="label" for="email">
              <span class="label-text">Email</span>
            </label>
            <div class="relative">
              <input
                id="email"
                type="email"
                placeholder="you@example.com"
                class="input input-bordered w-full pr-10"
                value={{this.email}}
                required="true"
                {{on "input" this.updateEmail}}
              />
              {{#if this.email}}
                <button
                  type="button"
                  class="btn btn-ghost btn-xs absolute top-1/2 right-2 -translate-y-1/2"
                  title="Clear email"
                  {{on "click" this.clearEmail}}
                >
                  <Icon @icon={{XIcon}} class="h-4 w-4" />
                </button>
              {{/if}}
            </div>
          </div>

          <div class="form-control mb-4">
            <label class="label" for="password">
              <span class="label-text">Password</span>
            </label>
            <div class="relative">
              <input
                id="password"
                type={{if this.showPassword "text" "password"}}
                placeholder="••••••••"
                class="input input-bordered w-full pr-10"
                value={{this.password}}
                required="true"
                {{on "input" this.updatePassword}}
              />
              <button
                type="button"
                class="btn btn-ghost btn-xs absolute top-1/2 right-2 -translate-y-1/2"
                title={{if this.showPassword "Hide password" "Show password"}}
                {{on "click" this.toggleShowPassword}}
              >
                {{#if this.showPassword}}
                  <Icon @icon={{EyeOffIcon}} class="h-4 w-4" />
                {{else}}
                  <Icon @icon={{EyeIcon}} class="h-4 w-4" />
                {{/if}}
              </button>
            </div>
          </div>

          <div class="form-control mb-6">
            <label class="label cursor-pointer justify-start gap-3">
              <input
                type="checkbox"
                class="checkbox checkbox-primary checkbox-sm"
                checked={{this.rememberMe}}
                {{on "change" this.toggleRememberMe}}
              />
              <span class="label-text">Remember me</span>
            </label>
          </div>

          <button
            type="submit"
            class="btn btn-primary w-full"
            disabled={{this.isLoading}}
          >
            {{#if this.isLoading}}
              <span class="loading loading-spinner loading-sm"></span>
              Logging in…
            {{else}}
              Login
            {{/if}}
          </button>
        </form>

        <div class="divider text-base-content/50 my-4 text-xs">or</div>

        <button
          type="button"
          class="btn btn-outline w-full gap-2"
          disabled={{this.isGoogleLoading}}
          {{on "click" this.handleGoogleLogin}}
        >
          {{#if this.isGoogleLoading}}
            <span class="loading loading-spinner loading-sm"></span>
          {{else}}
            <Icon @icon={{GoogleIcon}} class="h-4 w-4" />
          {{/if}}
          Continue with Google
        </button>
      </div>
    </div>
  </template>
}
